import React, { useCallback, useEffect, useMemo } from 'react';

import { useIntl } from 'react-intl';
import { graphql, type PreloadedQuery, usePreloadedQuery } from 'react-relay';

import { fg } from '@atlaskit/platform-feature-flags';

import { universalTypeFilterId } from '../../../common/constants/filters';
import type { NounKeys } from '../../../common/constants/nouns';
import { is3pProductKey, type ProductKeys } from '../../../common/constants/products';
import {
	type ThirdPartyConfigsBootstrap,
	type ThirdPartyConfigsWithAPIResponse,
} from '../../../common/constants/schemas/3p-config';
import { BrowserExtensionBanner } from '../../../common/ui/browser-extension-banner';
import { AppFilters } from '../../../common/ui/filters/app-filter-sidebar/app-filters';
import type { Sections } from '../../../common/ui/filters/app-filter-sidebar/app-filters/dynamic-sections';
import { NounFilters } from '../../../common/ui/filters/app-filter-sidebar/noun-filters';
import { SidebarContainer } from '../../../common/ui/filters/app-filter-sidebar/sidebar-container';
import { SidebarLoading } from '../../../common/ui/filters/app-filter-sidebar/sidebar-loading';
import { GiveFeedback, type GiveFeedbackProps } from '../../../common/ui/give-feedback';
import { useAppFilterState } from '../../../common/ui/right-panel';
import { isProductConnected } from '../../../common/utils/connectors';
import { onAppFilterShowHideClicked } from '../../../common/utils/events/app-filter-show-hide-clicked';
import { onAppFilterOptionClicked } from '../../../common/utils/events/product-option-clicked';
import {
	getUnifiedSearchRightRailEnhancementsExpValue,
	serveNounResultCounts,
	serveRightRailRecommendationsImprovementsWithExposure,
	serveWebPagesInTypeFilter,
} from '../../../common/utils/experiments';
import { serveFederatedSlackExperiment } from '../../../common/utils/experiments/experiment-utils';
import { useSafeIntercom } from '../../../common/utils/use-safe-intercom';
import { useAnalytics, useCommonAndPrivateAttributes } from '../../../controllers/analytics';
import { useFireVisibleProductsToAuthShown } from '../../../controllers/analytics/use-visible-products-to-auth-shown';
import { ErrorBoundary } from '../../../controllers/error-boundary';
import { useSelectFilter } from '../../../controllers/filters/base-select-filter';
import { useIsUserOAuthed, useOAuthHandlers } from '../../../controllers/oauth';
import { useRightPanelRecommendations } from '../../../controllers/recommened3pApps';
import { useSearchActions } from '../../../controllers/store/search';

import type {
	rightPanelAsyncQuery,
	rightPanelAsyncQuery$data,
} from './__generated__/rightPanelAsyncQuery.graphql';
import type {
	rightPanelQuery,
	rightPanelQuery$data,
} from './__generated__/rightPanelQuery.graphql';
import { messages } from './messages';

export type RightPanelProps = GiveFeedbackProps & {
	data?: rightPanelQuery$data | rightPanelAsyncQuery$data;
	searchQuery: string;
	thirdPartyConfigs: ThirdPartyConfigsBootstrap;
	availableProducts: ProductKeys[];
	availableNouns: NounKeys[];
	selectedProduct?: ProductKeys;
	selectedDatasource?: ThirdPartyConfigsWithAPIResponse;
	isNounsAvailable?: boolean;
	federatedSlackCount?: number | null;
	federatedOutlookMailCount?: number | null;
	federatedGmailCount?: number | null;
	hasMoreGmailResults?: boolean;
	hasMoreOutlookMailResults?: boolean;
	personalisedProductSections?: Map<Sections, ProductKeys[]>;
	setPersonalisedProductSections?: (sections: Map<Sections, ProductKeys[]>) => void;
	productsWithTimeout?: ProductKeys[];
	resultCountsLoading?: boolean;
};

export const RightPanelSkeleton = (): React.JSX.Element => {
	const { formatMessage } = useIntl();
	return (
		<SidebarContainer
			ariaLabel={formatMessage(messages.groupLabel)}
			data-testid="app-filter-sidebar-loading"
		>
			<SidebarLoading />
		</SidebarContainer>
	);
};

export const RightPanel = ({
	data,
	cloudId,
	searchQuery,
	primaryProduct,
	experimentConfig,
	selectedProducts,
	user,
	appMode,
	thirdPartyConfigs,
	availableProducts,
	availableNouns,
	selectedProduct,
	selectedDatasource,
	isNounsAvailable = false,
	federatedSlackCount,
	federatedOutlookMailCount,
	federatedGmailCount,
	hasMoreGmailResults,
	hasMoreOutlookMailResults,
	personalisedProductSections,
	setPersonalisedProductSections,
	productsWithTimeout,
	resultCountsLoading,
}: RightPanelProps): React.JSX.Element => {
	useEffect(() => {
		serveNounResultCounts(true);
		serveWebPagesInTypeFilter(true);
	}, []);

	const totalCounts = data?.search?.results?.totalCounts;

	const productResultCountMap = useMemo(() => {
		if (!totalCounts) {
			return {};
		}

		const countMap = totalCounts.reduce(
			(prev, { product, count }) => ({ ...prev, [product]: count }),
			{} as Record<string, number>,
		);

		// the backend returns townsquare-goals and townsquare-projects but the
		// frontend expects goal and project, so we need to map them here.
		if (serveNounResultCounts()) {
			if ('townsquare-goal' in countMap) {
				countMap['goal'] = countMap['townsquare-goal'];
				delete countMap['townsquare-goal'];
			}
			if ('townsquare-project' in countMap) {
				countMap['project'] = countMap['townsquare-project'];
				delete countMap['townsquare-project'];
			}
		}

		// Combine passionfruit counts with mercury (Focus product)
		if ('passionfruit' in countMap && fg('platform_passionfruit_search_onboarding_enabled')) {
			countMap['mercury'] = countMap['mercury'] + countMap['passionfruit'];
			delete countMap['passionfruit'];
		}

		// Special handling for Slack: use federatedSlackCount when available
		const isSlackFederatedSearchEnabled = serveFederatedSlackExperiment(
			selectedProduct,
			undefined,
			{ fireExperimentExposure: false },
			thirdPartyConfigs,
		);
		if (
			federatedSlackCount !== null &&
			federatedSlackCount !== undefined &&
			'slack' in countMap &&
			isSlackFederatedSearchEnabled
		) {
			countMap['slack'] = federatedSlackCount;
		}

		// Special handling for Outlook Mail: use federatedOutlookMailCount when available.
		// Outlook Mail uses the same selected-product federated count behavior as Gmail.
		if (federatedOutlookMailCount !== null && federatedOutlookMailCount !== undefined) {
			countMap['outlook-mail'] = federatedOutlookMailCount;
		}

		// Special handling for Gmail: use federatedGmailCount when available.
		// Gmail is a federated connector (isFederated: true) so it is excluded from thirdPartyFilters
		// in the right panel count query — meaning the aggregator never returns a totalCounts entry
		// for Gmail. We therefore set the count directly without checking 'gmail' in countMap.
		if (federatedGmailCount !== null && federatedGmailCount !== undefined) {
			countMap['gmail'] = federatedGmailCount;
		}

		return countMap;
	}, [
		totalCounts,
		federatedSlackCount,
		federatedOutlookMailCount,
		federatedGmailCount,
		selectedProduct,
		thirdPartyConfigs,
	]);

	const { formatMessage } = useIntl();
	const {
		selectProductFilter,
		unselectProductFilter,
		unsafe_setAtlasNouns,
		unsafe_setWebPageType,
	} = useSearchActions();
	const intercom = useSafeIntercom();
	const { fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes, nonPrivacySafeAttributes } = useCommonAndPrivateAttributes();
	const [universalTypeFilterData] = useSelectFilter({ filterId: universalTypeFilterId });

	const [filters, { setFilterHidden }] = useAppFilterState();
	const { recommended3pApps } = useRightPanelRecommendations({
		userId: user?.id,
		showRecommendations: serveRightRailRecommendationsImprovementsWithExposure(),
	});
	// In the prequery state (aka initial state), there is no data from the query
	const isInitialState = !data;

	const getIsConnected = useCallback(
		(productKey: ProductKeys | undefined): boolean =>
			isProductConnected(productKey, thirdPartyConfigs),
		[thirdPartyConfigs],
	);

	const onSelectProductWithAnalytics = useCallback(
		(
			newProduct: ProductKeys | undefined,
			datasource: string | ThirdPartyConfigsWithAPIResponse | undefined,
		) => {
			if (!newProduct && selectedProduct) {
				unselectProductFilter();
				fireAnalyticsEvent(
					onAppFilterOptionClicked({
						commonAttributes,
						nonPrivacySafeAttributes,
						productKey: undefined,
						isConnected: true,
					}),
				);
				return;
			}

			selectProductFilter(newProduct, datasource);

			const datasourceId = typeof datasource === 'string' ? datasource : datasource?.datasourceId;
			fireAnalyticsEvent(
				onAppFilterOptionClicked({
					commonAttributes,
					nonPrivacySafeAttributes,
					productKey: newProduct,
					isConnected: getIsConnected(newProduct),
					...(datasourceId && { datasourceId }),
				}),
			);

			if (newProduct && is3pProductKey(newProduct)) {
				intercom?.trackEvent('product_filter_clicked_3p', {
					product_filter: newProduct,
				});
			} else {
				intercom?.trackEvent('product_filter_clicked', {
					product_filter: newProduct,
				});
			}
		},
		[
			selectedProduct,
			unselectProductFilter,
			selectProductFilter,
			fireAnalyticsEvent,
			commonAttributes,
			nonPrivacySafeAttributes,
			intercom,
			getIsConnected,
		],
	);

	const appFilterProps = useMemo(
		() => ({
			searchQuery,
			thirdPartyConfigs,
			availableProducts,
			availableNouns,
			selectedProduct,
			selectedDatasource,
			isNounsAvailable,
			primaryProduct,
			cloudId,
			filterData: universalTypeFilterData,
			setAtlasNoun: unsafe_setAtlasNouns,
			setWebPageType: unsafe_setWebPageType,
			onSelectProduct: onSelectProductWithAnalytics,
			productResultCountMap: productResultCountMap ?? {},
			sidebarStoredState: filters,
			onFilterHide: (productKey: ProductKeys) => {
				setFilterHidden(productKey, true);
				fireAnalyticsEvent(
					onAppFilterShowHideClicked({
						commonAttributes,
						nonPrivacySafeAttributes,
						productKey,
						hidden: true,
						isConnected: getIsConnected(productKey),
					}),
				);
			},
			onFilterShow: (productKey: ProductKeys) => {
				setFilterHidden(productKey, false);
				fireAnalyticsEvent(
					onAppFilterShowHideClicked({
						commonAttributes,
						nonPrivacySafeAttributes,
						productKey,
						hidden: false,
						isConnected: getIsConnected(productKey),
					}),
				);
			},
			useOAuthHandlers,
			useIsUserOAuthed,
			ErrorBoundary,
			isInitialState,
			recommended3pApps,
			onVisibleProductsToAuthShown: useFireVisibleProductsToAuthShown,
			personalisedProductSections,
			setPersonalisedProductSections,
			productsWithTimeout,
			resultCountsLoading,
			hasMoreGmailResults,
			hasMoreOutlookMailResults,
		}),
		[
			availableProducts,
			availableNouns,
			selectedProduct,
			selectedDatasource,
			onSelectProductWithAnalytics,
			unsafe_setWebPageType,
			isNounsAvailable,
			universalTypeFilterData,
			unsafe_setAtlasNouns,
			productResultCountMap,
			filters,
			setFilterHidden,
			thirdPartyConfigs,
			fireAnalyticsEvent,
			commonAttributes,
			nonPrivacySafeAttributes,
			searchQuery,
			primaryProduct,
			cloudId,
			isInitialState,
			recommended3pApps,
			personalisedProductSections,
			setPersonalisedProductSections,
			productsWithTimeout,
			resultCountsLoading,
			hasMoreGmailResults,
			hasMoreOutlookMailResults,
			getIsConnected,
		],
	);

	return (
		<SidebarContainer
			ariaLabel={formatMessage(messages.groupLabel)}
			data-testid="app-filter-sidebar"
		>
			<BrowserExtensionBanner fireAnalyticsEvent={fireAnalyticsEvent} />
			<ErrorBoundary section="SearchPageAppFilter">
				<AppFilters {...appFilterProps} />
			</ErrorBoundary>
			{/* Hide NounFilters for single-select cohort - nouns are merged into Show More popup */}
			{getUnifiedSearchRightRailEnhancementsExpValue() !== 'single-select' && (
				<NounFilters {...appFilterProps} onSelectProduct={onSelectProductWithAnalytics} />
			)}
			<GiveFeedback
				cloudId={cloudId}
				primaryProduct={primaryProduct}
				experimentConfig={experimentConfig}
				selectedProducts={selectedProducts}
				user={user}
				appMode={appMode}
				isCQL={commonAttributes.isCQL}
			/>
		</SidebarContainer>
	);
};

export const RightPanelContainer = ({
	rightPanelQuery,
	...props
}: Omit<RightPanelProps, 'data'> & {
	rightPanelQuery: PreloadedQuery<rightPanelQuery>;
}): React.JSX.Element => {
	const queryData = usePreloadedQuery<rightPanelQuery>(
		graphql`
			query rightPanelQuery(
				$query: String!
				$cloudIdARI: String!
				$limit: Int!
				$entities: [String!]!
				$queryVersion: Int
				$searchSessionId: String
				$sessionId: String
				$commonFilters: SearchCommonFilter
				$confluenceFilters: SearchConfluenceFilter
				$jiraFilters: SearchJiraFilter
				$assetsFilters: SearchAssetsFilter
				$mercuryFilters: SearchMercuryFilter
				$talentFilters: SearchTalentFilter
				$compassFilters: SearchCompassFilter
				$thirdPartyFilters: SearchThirdPartyFilter
				$sort: [SearchSortInput]
				$sourceProduct: String
				$experimentId: String
				$shadowExperimentId: String
				$experimentLayers: [SearchExperimentLayer]
				$disableQueryReplacement: Boolean
				$experience: String!
			) {
				search {
					results: search(
						experience: $experience
						query: $query
						first: $limit
						sort: $sort
						disableQueryReplacement: $disableQueryReplacement
						filters: {
							entities: $entities
							locations: [$cloudIdARI]
							commonFilters: $commonFilters
							confluenceFilters: $confluenceFilters
							jiraFilters: $jiraFilters
							mercuryFilters: $mercuryFilters
							compassFilters: $compassFilters
							thirdPartyFilters: $thirdPartyFilters
							assetsFilters: $assetsFilters
							talentFilters: $talentFilters
						}
						analytics: {
							queryVersion: $queryVersion
							searchSessionId: $searchSessionId
							sourceProduct: $sourceProduct
							sessionId: $sessionId
						}
						experimentContext: {
							experimentId: $experimentId
							shadowExperimentId: $shadowExperimentId
							experimentLayers: $experimentLayers
						}
					) {
						totalCounts {
							product
							count
						}
					}
				}
			}
		`,
		rightPanelQuery,
	);

	return <RightPanel {...props} data={queryData} />;
};

export const RightPanelAsyncContainer = ({
	rightPanelQuery: rightPanelAsyncQueryRef,
	...props
}: Omit<RightPanelProps, 'data'> & {
	rightPanelQuery: PreloadedQuery<rightPanelAsyncQuery>;
}): React.JSX.Element => {
	const queryData = usePreloadedQuery<rightPanelAsyncQuery>(
		graphql`
			query rightPanelAsyncQuery(
				$query: String!
				$cloudIdARI: String!
				$limit: Int!
				$entities: [String!]!
				$queryVersion: Int
				$searchSessionId: String
				$sessionId: String
				$commonFilters: SearchCommonFilter
				$confluenceFilters: SearchConfluenceFilter
				$jiraFilters: SearchJiraFilter
				$assetsFilters: SearchAssetsFilter
				$mercuryFilters: SearchMercuryFilter
				$talentFilters: SearchTalentFilter
				$compassFilters: SearchCompassFilter
				$thirdPartyFilters: SearchThirdPartyFilter
				$sort: [SearchSortInput]
				$sourceProduct: String
				$experimentId: String
				$shadowExperimentId: String
				$experimentLayers: [SearchExperimentLayer]
				$disableQueryReplacement: Boolean
				$experience: String!
				$expectResult: Boolean
			) {
				search {
					results: asyncSearch(
						experience: $experience
						expectResult: $expectResult
						query: $query
						first: $limit
						sort: $sort
						disableQueryReplacement: $disableQueryReplacement
						filters: {
							entities: $entities
							locations: [$cloudIdARI]
							commonFilters: $commonFilters
							confluenceFilters: $confluenceFilters
							jiraFilters: $jiraFilters
							mercuryFilters: $mercuryFilters
							compassFilters: $compassFilters
							thirdPartyFilters: $thirdPartyFilters
							assetsFilters: $assetsFilters
							talentFilters: $talentFilters
						}
						analytics: {
							queryVersion: $queryVersion
							searchSessionId: $searchSessionId
							sourceProduct: $sourceProduct
							sessionId: $sessionId
						}
						experimentContext: {
							experimentId: $experimentId
							shadowExperimentId: $shadowExperimentId
							experimentLayers: $experimentLayers
						}
					) {
						isCachedResult
						totalCounts {
							product
							count
						}
					}
				}
			}
		`,
		rightPanelAsyncQueryRef,
	);

	void queryData.search?.results?.isCachedResult;

	return <RightPanel {...props} data={queryData} />;
};

/**
 * Resolves which right panel query container to use based on the search_async_ssr feature gate.
 * Only one of the two query types ever exists at a time (determined by the FG),
 * so a single union-typed ref is sufficient.
 */
export const RightPanelContainerResolver = ({
	rightPanelQueryRef,
	...props
}: Omit<RightPanelProps, 'data'> & {
	rightPanelQueryRef: PreloadedQuery<rightPanelQuery> | PreloadedQuery<rightPanelAsyncQuery>;
}): React.JSX.Element => {
	if (fg('search_async_ssr')) {
		return (
			<RightPanelAsyncContainer
				rightPanelQuery={rightPanelQueryRef as PreloadedQuery<rightPanelAsyncQuery>}
				{...props}
			/>
		);
	}

	return (
		<RightPanelContainer
			rightPanelQuery={rightPanelQueryRef as PreloadedQuery<rightPanelQuery>}
			{...props}
		/>
	);
};
